import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { 
  Shield, 
  ArrowLeft, 
  Download, 
  CheckCircle, 
  Clock,
  Smartphone,
  Laptop,
  Tablet,
  QrCode
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';

const DeviceEnrollmentPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [deviceName, setDeviceName] = useState('');
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [enrollmentUrl, setEnrollmentUrl] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [enrollmentToken, setEnrollmentToken] = useState('');

  const steps = [
    { number: 1, title: 'Device Information', description: 'Enter device details' },
    { number: 2, title: 'Terms & Conditions', description: 'Accept enrollment terms' },
    { number: 3, title: 'MDM Profile Installation', description: 'Install management profile' },
    { number: 4, title: 'Enrollment Complete', description: 'Device successfully enrolled' }
  ];

  const handleDeviceNameSubmit = () => {
    if (!deviceName.trim()) {
      toast.error('Please enter a device name');
      return;
    }
    setCurrentStep(2);
  };

  const handleTermsAcceptance = () => {
    if (!acceptedTerms) {
      toast.error('Please accept the terms and conditions');
      return;
    }
    setCurrentStep(3);
    generateEnrollmentUrl();
  };

  const generateEnrollmentUrl = async () => {
    setIsLoading(true);
    
    try {
      const { data: user } = await supabase.auth.getUser();
      if (!user.user) throw new Error('Not authenticated');

      // Call the enrollment function
      const { data, error } = await supabase.functions.invoke('portal-device-enrollment', {
        body: {
          device_name: deviceName,
          portal_user_id: user.user.id
        }
      });

      if (error) throw error;

      setEnrollmentToken(data.enrollment_token);
      setEnrollmentUrl(data.enrollment_url);
    } catch (error: any) {
      console.error('Enrollment error:', error);
      toast.error('Failed to generate enrollment URL');
    } finally {
      setIsLoading(false);
    }
  };

  const copyEnrollmentUrl = async () => {
    try {
      await navigator.clipboard.writeText(enrollmentUrl);
      toast.success('Enrollment URL copied to clipboard');
    } catch (error) {
      toast.error('Failed to copy URL');
    }
  };

  const handleEnrollmentComplete = () => {
    setCurrentStep(4);
    toast.success('Device enrollment initiated!');
  };

  const StepIndicator = ({ step, isActive, isCompleted }: { step: typeof steps[0], isActive: boolean, isCompleted: boolean }) => (
    <div className={`flex items-center ${isActive ? 'text-primary' : isCompleted ? 'text-green-600' : 'text-muted-foreground'}`}>
      <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
        isActive ? 'border-primary bg-primary text-primary-foreground' : 
        isCompleted ? 'border-green-600 bg-green-600 text-white' : 
        'border-muted-foreground'
      }`}>
        {isCompleted ? <CheckCircle className="h-4 w-4" /> : step.number}
      </div>
      <div className="ml-3">
        <p className="font-medium">{step.title}</p>
        <p className="text-sm text-muted-foreground">{step.description}</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b bg-card">
        <div className="container mx-auto px-2 sm:px-4 py-4">
          <div className="flex items-center space-x-2 sm:space-x-4">
            <Link to="/portal/mydevices">
              <Button variant="ghost" size="sm">
                <ArrowLeft className="h-4 w-4 sm:mr-2" />
                <span className="hidden sm:inline">Back to Devices</span>
              </Button>
            </Link>
            <img 
              src="/lovable-uploads/5b1ac4fa-502a-48e9-b8b6-7c1fe37496e8.png" 
              alt="smplify Logo" 
              className="h-6 sm:h-8"
            />
            <span className="hidden sm:inline text-muted-foreground">Device Enrollment</span>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-2 sm:px-4 py-4 sm:py-8 max-w-4xl">
        {/* Progress Steps */}
        <div className="mb-8">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {steps.map((step) => (
              <StepIndicator
                key={step.number}
                step={step}
                isActive={currentStep === step.number}
                isCompleted={currentStep > step.number}
              />
            ))}
          </div>
        </div>

        {/* Step Content */}
        <Card>
          {currentStep === 1 && (
            <>
              <CardHeader>
                <CardTitle>Device Information</CardTitle>
                <CardDescription>
                  Enter a name for your device to help identify it in your dashboard
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="deviceName">Device Name</Label>
                  <Input
                    id="deviceName"
                    placeholder="e.g., John's MacBook Pro, Sarah's iPhone"
                    value={deviceName}
                    onChange={(e) => setDeviceName(e.target.value)}
                  />
                  <p className="text-sm text-muted-foreground">
                    Choose a descriptive name to easily identify this device
                  </p>
                </div>

                <Alert>
                  <AlertDescription>
                    This device will be enrolled for management and monitoring. 
                    You'll be able to perform actions like remote lock, wipe, and location tracking.
                  </AlertDescription>
                </Alert>

                <Button onClick={handleDeviceNameSubmit} className="w-full">
                  Continue
                </Button>
              </CardContent>
            </>
          )}

          {currentStep === 2 && (
            <>
              <CardHeader>
                <CardTitle>Terms & Conditions</CardTitle>
                <CardDescription>
                  Please review and accept the device enrollment terms
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="bg-muted p-4 rounded-lg max-h-60 overflow-y-auto">
                  <h4 className="font-semibold mb-2">Device Management Agreement</h4>
                  <div className="space-y-2 text-sm">
                    <p>By enrolling this device, you agree to:</p>
                    <ul className="list-disc list-inside space-y-1 ml-4">
                      <li>Allow remote management and monitoring of this device</li>
                      <li>Permit installation of management profiles and certificates</li>
                      <li>Enable location services for device tracking when needed</li>
                      <li>Allow remote actions including lock, wipe, and reboot</li>
                      <li>Comply with organizational security policies</li>
                      <li>Accept that admin approval is required for enrollment</li>
                    </ul>
                    <p className="mt-4">
                      This device will be subject to management policies that may restrict 
                      certain functions and monitor device usage for security purposes.
                    </p>
                  </div>
                </div>

                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="enrollmentTerms"
                    checked={acceptedTerms}
                    onCheckedChange={(checked) => setAcceptedTerms(checked as boolean)}
                  />
                  <Label htmlFor="enrollmentTerms" className="text-sm">
                    I have read and accept the device management terms and conditions
                  </Label>
                </div>

                <Button 
                  onClick={handleTermsAcceptance} 
                  className="w-full"
                  disabled={!acceptedTerms}
                >
                  Accept & Continue
                </Button>
              </CardContent>
            </>
          )}

          {currentStep === 3 && (
            <>
              <CardHeader>
                <CardTitle>Install MDM Profile</CardTitle>
                <CardDescription>
                  Download and install the management profile on your device
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-6">
                {isLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto"></div>
                    <p className="mt-2 text-muted-foreground">Generating enrollment profile...</p>
                  </div>
                ) : enrollmentUrl ? (
                  <>
                    <Alert>
                      <Download className="h-4 w-4" />
                      <AlertDescription>
                        Your enrollment profile is ready. Click the link below to download and install it on your device.
                      </AlertDescription>
                    </Alert>

                    <div className="bg-muted p-4 rounded-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium">Enrollment Profile</p>
                          <p className="text-sm text-muted-foreground">Device: {deviceName}</p>
                        </div>
                        <QrCode className="h-8 w-8" />
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <Button onClick={copyEnrollmentUrl} variant="outline">
                          Copy Enrollment URL
                        </Button>
                        <Button onClick={() => window.open(enrollmentUrl, '_blank')}>
                          <Download className="h-4 w-4 mr-2" />
                          Download Profile
                        </Button>
                      </div>

                      <Alert>
                        <AlertDescription>
                          <strong>Installation Instructions:</strong>
                          <ol className="list-decimal list-inside mt-2 space-y-1">
                            <li>Click "Download Profile" or use the copied URL</li>
                            <li>Follow your device's prompts to install the profile</li>
                            <li>Enter your device passcode when prompted</li>
                            <li>Trust the management profile when asked</li>
                          </ol>
                        </AlertDescription>
                      </Alert>

                      <Button onClick={handleEnrollmentComplete} className="w-full">
                        I've Installed the Profile
                      </Button>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <p className="text-muted-foreground">Failed to generate enrollment profile. Please try again.</p>
                    <Button onClick={generateEnrollmentUrl} className="mt-4">
                      Retry
                    </Button>
                  </div>
                )}
              </CardContent>
            </>
          )}

          {currentStep === 4 && (
            <>
              <CardHeader className="text-center">
                <CheckCircle className="h-12 w-12 text-green-600 mx-auto mb-4" />
                <CardTitle>Enrollment Complete!</CardTitle>
                <CardDescription>
                  Your device "{deviceName}" has been submitted for enrollment
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4 text-center">
                <Alert>
                  <Clock className="h-4 w-4" />
                  <AlertDescription>
                    Your device enrollment is pending admin approval. 
                    You'll receive an email notification once your device is approved and ready for management.
                  </AlertDescription>
                </Alert>

                <div className="space-y-2">
                  <p className="text-sm text-muted-foreground">
                    What happens next:
                  </p>
                  <ul className="text-sm text-left space-y-1">
                    <li>• Admin reviews your enrollment request</li>
                    <li>• Device is approved and activated for management</li>
                    <li>• You can then use device actions (lock, wipe, locate)</li>
                    <li>• Device appears in your dashboard with full controls</li>
                  </ul>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
                  <Link to="/portal/mydevices">
                    <Button variant="outline" className="w-full">
                      View My Devices
                    </Button>
                  </Link>
                  <Link to="/portal/enroll">
                    <Button className="w-full">
                      Enroll Another Device
                    </Button>
                  </Link>
                </div>
              </CardContent>
            </>
          )}
        </Card>
      </div>
    </div>
  );
};

export default DeviceEnrollmentPage;